import { Loader2, Unlink } from "lucide-react";
import type { McpConnector } from "@/services/mcp-gateway";

/**
 * "GitHub" is two connections wearing one name: the Git connection (repos, PRs,
 * pipelines) and the GitHub MCP connector (the agent's tools). Disconnect Git only ever
 * cleared the first, so people disconnected GitHub, saw Weave still showing a green
 * GitHub dot, and reasonably read it as a bug — the dot was honest, the split was
 * invisible. This says out loud that the second connection exists and offers to take it
 * too, rather than silently severing tools the agent may be relying on.
 */
export function DisconnectGitDialog({
  githubConnector,
  busy,
  onDisconnectGitOnly,
  onDisconnectBoth,
  onCancel,
}: {
  githubConnector: McpConnector;
  busy: boolean;
  onDisconnectGitOnly: () => void;
  onDisconnectBoth: () => void;
  onCancel: () => void;
}) {
  const toolCount = githubConnector.tool_count;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="disconnect-git-title"
    >
      {/*
        NOT `bg-card`: inside `.studio-shell`, any `.border.bg-card` is repainted with the
        theme's translucent panel gradient (bottom stop ~0.84 alpha, `!important`). That is
        right for a panel sitting on the page and wrong for a dialog stacked over the repo
        list — the rows read straight through the text. The arbitrary value paints the same
        colour without matching that selector.
      */}
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-[hsl(var(--card))] shadow-[0_24px_64px_rgba(0,0,0,0.5)]">
        <div className="px-6 pb-4 pt-5">
          <h2 id="disconnect-git-title" className="text-base font-semibold">
            Disconnect Git
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            This clears the repository and branch you work on here.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            GitHub is also connected under MCP as{" "}
            <span className="font-medium text-foreground">{githubConnector.name}</span>
            {toolCount > 0 && (
              <>
                {" "}— {toolCount} tool{toolCount === 1 ? "" : "s"} Weave can call
              </>
            )}
            . That connection is separate and keeps working unless you remove it too.
          </p>
        </div>
        <div className="flex flex-col gap-2 border-t border-border px-6 py-4 sm:flex-row sm:justify-end">
          <button type="button" onClick={onCancel} disabled={busy} className="studio-button">
            Cancel
          </button>
          <button
            type="button"
            onClick={onDisconnectGitOnly}
            disabled={busy}
            className="studio-button text-muted-foreground hover:text-destructive"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
            Disconnect Git only
          </button>
          <button
            type="button"
            onClick={onDisconnectBoth}
            disabled={busy}
            className="studio-button studio-button-primary"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
            Disconnect both
          </button>
        </div>
      </div>
    </div>
  );
}
